import { Component, input, output, OnDestroy, inject } from '@angular/core';

import { Router } from '@angular/router';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Subject, takeUntil } from 'rxjs';

import {
  BemReportAvailableNotificationDto,
  FieldObservationAddedNotificationDto,
  NotificationDto,
  NotificationType,
  SatelliteImagesAvailableNotificationDto,
  TgaSteps,
  hasDateAndCropField,
} from '../../shared/models/models';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'app-notification-message-card',
  templateUrl: './notification-message-card.component.html',
  styleUrls: ['./notification-message-card.component.scss'],
  imports: [
    MatIconButton,
    MatIcon,
    TranslatePipe,
  ],
})
export class NotificationMessageCardComponent implements OnDestroy {
  private readonly localeService = inject(LocaleService);
  private readonly router = inject(Router);

  notification = input.required<NotificationDto>();
  notificationDismissed = output<NotificationDto>();

  private readonly destroy$ = new Subject<void>();
  locale: string;

  constructor() {
    this.locale = this.localeService.getLocale();
    this.localeService.localeDataChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.locale = this.localeService.getLocale();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onArrowClick(): void {
    const notification = this.notification();

    switch (notification.type) {
      case NotificationType.BemReportAvailable:
      case NotificationType.SupplementaryFertilizerAdvised: {
        const singleCropFieldNotification = this.asSingleCropFieldNotification(notification);
        this.router.navigate(['/bem', singleCropFieldNotification.cropFieldId]);
        break;
      }
      case NotificationType.SatelliteImagesAvailable:
      case NotificationType.FieldObservationAdded: {
        const singleCropFieldNotification = this.asSingleCropFieldNotification(notification);
        const cropYear = new Date(singleCropFieldNotification.createdAt).getFullYear();
        this.router.navigate(['/tga/cropfields', TgaSteps.Growing, 'advice', cropYear, singleCropFieldNotification.cropFieldId]);
        break;
      }
    }

    if (notification.canBeClosed) {
      this.notificationDismissed.emit(notification);
    }
  }

  getCropFieldName(): string {
    const notification = this.notification();
    return hasDateAndCropField(notification) ? notification.cropFieldName : '';
  }

  asSingleCropFieldNotification(notification: NotificationDto) {
    return notification as BemReportAvailableNotificationDto |
      SatelliteImagesAvailableNotificationDto |
      FieldObservationAddedNotificationDto;
  }

  shouldShowDate(): boolean {
    const notification = this.notification();
    return hasDateAndCropField(notification) && notification.type !== NotificationType.SatelliteImagesAvailable;
  }

  getDate(): string {
    if (!this.shouldShowDate()) {
      return '';
    }

    const notification = this.notification();
    const date = new Date(notification.createdAt);
    return date.toLocaleDateString(this.locale, { day: 'numeric', month: 'long', year: 'numeric' });
  }
}
